import Bugsnag, { Event } from "@bugsnag/js";
import BugsnagPluginReact, {
  BugsnagErrorBoundary,
} from "@bugsnag/plugin-react";
import React, { ComponentProps, FunctionComponent } from "react";
import {
  isStorybook,
  isTest,
  VITE_APP_VERSION,
  VITE_BUGSNAG_API_KEY,
} from "./env";

// disable sending observation errors caused by react-virtual
// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
// also disable other benign resize observer error: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
const RESIZE_OBSERVER_BENIGN_ERROR_MESSAGES = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
] as const;

const eventMeetsDoNotSendConditions = (event: Event) =>
  [
    RESIZE_OBSERVER_BENIGN_ERROR_MESSAGES.some(
      (message) => message === event.errors[0]!.errorMessage
    ),
    // add any other event conditions we do not want to not send here...
  ].some(Boolean);

export const bugsnagClient = // disable bugsnag in test env and in stories, which report to nobody
  // https://github.com/bugsnag/bugsnag-js/issues/452
  isTest || isStorybook
    ? null
    : Bugsnag.start({
        apiKey: VITE_BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginReact()],
        releaseStage: import.meta.env.MODE,
        appVersion: VITE_APP_VERSION,
        onError: (event) => {
          if (eventMeetsDoNotSendConditions(event)) {
            return false;
          }
        },
      });

const ErrorBoundary =
  isTest || isStorybook
    ? null
    : Bugsnag.getPlugin("react")?.createErrorBoundary(React);
const Passthrough: FunctionComponent<
  React.PropsWithChildren<ComponentProps<BugsnagErrorBoundary>>
> = ({ children }) => <>{children}</>; // eslint-disable-line react/jsx-no-useless-fragment

export default ErrorBoundary ?? Passthrough;
